import React from "react";
import { Search } from "lucide-react";
import { EmptyState } from "../empty-state/EmptyState.jsx";

/**
 * Listen-Shell: Titel, Suche, Filter-Tabs, Leer/Lade, Content-Slot.
 * Fachliche Filter-/Card-Logik bleibt beim Aufrufer.
 *
 * @param {{
 *   titleId: string,
 *   title: string,
 *   headerAside?: React.ReactNode,
 *   searchValue?: string,
 *   onSearchChange?: (value: string) => void,
 *   searchPlaceholder?: string,
 *   searchAriaLabel?: string,
 *   archiveToggle?: { checked: boolean, onChange: (v: boolean) => void, label: string } | null,
 *   toolbarEnd?: React.ReactNode,
 *   filters?: Array<{ value: string, label: string, count?: number }>,
 *   filterValue?: string,
 *   onFilterChange?: (value: string) => void,
 *   filtersAriaLabel?: string,
 *   loading?: boolean,
 *   loadingMessage?: string,
 *   empty?: boolean,
 *   emptyMessage?: string,
 *   beforeList?: React.ReactNode,
 *   children?: React.ReactNode,
 *   className?: string,
 * }} props
 */
export function EntityListSection({
  titleId,
  title,
  headerAside = null,
  searchValue,
  onSearchChange,
  searchPlaceholder = "",
  searchAriaLabel = "",
  archiveToggle = null,
  toolbarEnd = null,
  filters = null,
  filterValue,
  onFilterChange,
  filtersAriaLabel = "",
  loading = false,
  loadingMessage = "",
  empty = undefined,
  emptyMessage = "",
  beforeList = null,
  children = null,
  className = "",
}) {
  const showSearch = typeof onSearchChange === "function";
  const showFilters = Array.isArray(filters) && filters.length > 0;
  const showToolbar = showSearch || archiveToggle || toolbarEnd;
  const isEmpty =
    !loading &&
    (typeof empty === "boolean"
      ? empty
      : children == null || children === false);

  return (
    <section
      className={`mm-hub__tournaments ${className}`.trim()}
      aria-labelledby={titleId}
    >
      <div className="mm-hub__tournamentsHeader">
        <h2 id={titleId} className="mm-hub__tournamentsTitle">
          {title}
        </h2>
        {headerAside}
      </div>

      {showToolbar ? (
        <div className="mm-hub__tournamentsToolbar">
          {showSearch ? (
            <label
              className="mm-hub__tournamentsSearch"
              aria-label={searchAriaLabel || searchPlaceholder}
            >
              <Search size={16} strokeWidth={2} aria-hidden="true" />
              <input
                type="search"
                className="mm-hub__tournamentsSearchInput"
                value={searchValue ?? ""}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </label>
          ) : null}
          {archiveToggle ? (
            <label className="mm-hub__tournamentsArchiveToggle">
              <input
                type="checkbox"
                checked={Boolean(archiveToggle.checked)}
                onChange={(e) => archiveToggle.onChange(e.target.checked)}
              />
              <span>{archiveToggle.label}</span>
            </label>
          ) : null}
          {toolbarEnd ? (
            <div className="mm-hub__tournamentsToolbarEnd">{toolbarEnd}</div>
          ) : null}
        </div>
      ) : null}

      {showFilters ? (
        <div
          className="mm-hub__tournamentsFilters"
          role="tablist"
          aria-label={filtersAriaLabel}
        >
          {filters.map((f) => {
            const isActive = filterValue === f.value;
            return (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`mm-hub__tournamentsFilter${isActive ? " is-active" : ""}`}
                onClick={() => onFilterChange?.(f.value)}
              >
                <span>{f.label}</span>
                {typeof f.count === "number" ? (
                  <span className="mm-hub__tournamentsFilterCount">
                    {f.count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {beforeList}

      {loading ? (
        <EmptyState
          variant="loading"
          message={loadingMessage}
          className="mm-hub__tournamentsEmpty"
        />
      ) : isEmpty ? (
        <EmptyState
          variant="empty"
          message={emptyMessage}
          className="mm-hub__tournamentsEmpty"
        />
      ) : (
        children
      )}
    </section>
  );
}
